import { ApiError } from "./ApiError";

const API_PATH_TEMPLATE = "https://{{type}}.howardhf.com:9443/api";
const API_PATH_DEFAULT = API_PATH_TEMPLATE.replace("{{type}}", "api");
let API_PATH = API_PATH_DEFAULT;

export const EVENT_SOURCE_URL = API_PATH + "/v0.2/event-stream";

export interface ISurvey {
	id?: string;
	name: {
		first: string;
		middle?: string;
		last: string;
	};
	phone: string;
	birthdate: string;
	email: string;
	timestamp: string;
	contactAllowed: boolean;
	healthfairId: string;
	healthFair: {
		id: string;
		name: string;
		scheduledDate: string;
		members: Array<{
			id: string;
			name: {
				first: string;
				middle?: string;
				last: string;
			};
		}>;
	};
	wantAppointment: boolean;
	address: {
		street: string;
		city: string;
		state: string;
		zip: string;
	};
	insurance: {
		provider: string;
		policyNumber: string;
		groupNumber: string;
		employer: string;
	};
	answers: ISurveyAnswer[];
	contactLater: string;
	hasAppointments?: boolean;
	sex?: string;
	surveyTypeId?: string;
	surveyName?: string;
	appointmentMode?: number;

	contactNotes?: any[];
	contactInfo?: any;

	source: number;
}

let ApiToken: string;
const ApiListeners: ApiEventHandler[] = [];

function emitEvent(eventType: ApiEvents) {
	for (const handler of ApiListeners) {
		handler(eventType);
	}
}

export type ApiEvents = "no_auth";
export type ApiEventHandler = (eventType: ApiEvents) => void;
type ProgressCallback = (current: number, total: number) => void;

export function apiSetToken(token: string) {
	ApiToken = token;
}

export interface ISurveyAnswer {
	id: string;
	displayText: string;
	answerText: string;
}

export function addApiEventListener(handler: ApiEventHandler) {
	ApiListeners.push(handler);
}

export function removeApiEventListener(handler: ApiEventHandler) {
	const index = ApiListeners.indexOf(handler);
	if (index >= 0) {
		ApiListeners.splice(index, 1);
	}
}

export function setApiTarget(endpoint?: string) {
	if (!endpoint) {
		API_PATH = API_PATH_DEFAULT;
	} else {
		API_PATH = API_PATH_TEMPLATE.replace("{{type}}", endpoint);
	}
}

function makeApiUrl(version: string, path: string[]) {
	path.unshift(version);
	path.unshift(API_PATH);

	return path.join("/");
}

function xhrFetch(
	path: string,
	options: RequestInit,
	progressCb?: ProgressCallback
): PromiseLike<Response> {
	const xhr = new XMLHttpRequest();

	return new Promise((resolve, reject) => {
		const eventHandler = !!options.body ? xhr.upload : xhr;

		xhr.onerror = () => reject("Failed to upload");
		eventHandler.onerror = () => reject("Failed to upload");
		eventHandler.onabort = () => reject("Upload canceled");
		eventHandler.ontimeout = () => reject("Timed out trying to upload");
		eventHandler.onprogress = (evt) => {
			if (progressCb) {
				progressCb(evt.loaded, evt.total);
			}
		};
		xhr.onload = () => {
			const headers = xhr
				.getAllResponseHeaders()
				.trim()
				.split(/[\r\n]+/)
				.filter((a) => !!a)
				.map((a) => {
					const parts = a.split(": ");
					const header = parts.shift() || "";
					const value = parts.join(": ");

					return [header, value];
				});

			resolve(
				new Response(
					!xhr.response || (xhr.responseType === "blob" && xhr.response.size === 0)
						? null
						: xhr.response,
					{
						status: xhr.status,
						statusText: xhr.statusText,
						headers: headers as any,
					}
				)
			);
		};

		xhr.responseType = "blob";

		xhr.open(options.method || "GET", path);

		if (options.headers) {
			for (const header of options.headers as string[][]) {
				xhr.setRequestHeader(header[0], header[1]);
			}
		}

		xhr.send((options.body || undefined) as any);
	});
}

async function apiBase<T>(
	path: string,
	method: string,
	version: string,
	body?: any,
	progressCb?: ProgressCallback
): Promise<T> {
	const isFile = body instanceof FormData || body instanceof File;

	if (isFile && body instanceof File) {
		// Wrap in FormData
		const fd = new FormData();
		fd.append("", body);

		body = fd;
	}

	const options: RequestInit = {
		body: body && isFile ? body : body ? JSON.stringify(body) : undefined,
		headers: [["Authorization", `Bearer ${ApiToken}`]],
		method,
		referrerPolicy: "no-referrer-when-downgrade",
	};
	if (!isFile) {
		(options.headers! as string[][]).push(["Content-Type", "application/json"]);
	}

	const resp = await (progressCb
		? xhrFetch(`${API_PATH}/v${version}/${path}`, options, progressCb)
		: fetch(`${API_PATH}/v${version}/${path}`, options));

	if (!resp.ok) {
		if (resp.status === 401) {
			emitEvent("no_auth");
		}

		const ct = resp.headers.get("content-type");
		if (ct && ct.includes("application/json")) {
			const data = await resp.json();

			throw new ApiError(
				resp.status,
				(data && (data.message || (data.error && data.error.message) || data.error)) ||
					resp.statusText
			);
		} else {
			throw new ApiError(resp.status, resp.statusText);
		}
	}

	const cType = resp.headers.get("content-type");
	if (cType) {
		if (cType.includes("application/json")) {
			return resp.json();
		} else {
			return resp.blob() as Promise<any>;
		}
	} else {
		return undefined as any as T;
	}
}

export async function apiGet<T>(path: string): Promise<T> {
	return apiBase<T>(path, "GET", "0.1");
}

export async function apiPost<T>(path: string, body?: any): Promise<T> {
	return apiBase<T>(path, "POST", "0.1", body);
}

export async function apiPut<T>(path: string, body?: any): Promise<T> {
	return apiBase<T>(path, "PUT", "0.1", body);
}

export async function apiPatch<T>(path: string, body: any): Promise<T> {
	return apiBase<T>(path, "PATCH", "0.1", body);
}

export async function apiDelete<T>(path: string): Promise<T> {
	return apiBase<T>(path, "DELETE", "0.1");
}

export function getAuthorizationHeader() {
	return ApiToken ? `Bearer ${ApiToken}` : null;
}

export const V1 = {
	delete: <T>(path: string) => apiBase<T>(path, "DELETE", "0.1"),
	get: <T>(path: string) => apiBase<T>(path, "GET", "0.1"),
	patch: <T>(path: string, body?: any) => apiBase<T>(path, "PATCH", "0.1", body),
	post: <T>(path: string, body?: any) => apiBase<T>(path, "POST", "0.1", body),
	put: <T>(path: string, body?: any) => apiBase<T>(path, "PUT", "0.1", body),

	makeUrl: (...path: string[]) => makeApiUrl("v0.1", path),
};

export const V2 = {
	delete: <T>(path: string) => apiBase<T>(path, "DELETE", "0.2"),
	get: <T>(path: string, progressCb?: ProgressCallback) =>
		apiBase<T>(path, "GET", "0.2", undefined, progressCb),
	patch: <T>(path: string, body?: any) => apiBase<T>(path, "PATCH", "0.2", body),
	post: <T>(path: string, body?: any, progressCb?: ProgressCallback) =>
		apiBase<T>(path, "POST", "0.2", body, progressCb),
	put: <T>(path: string, body?: any, progressCb?: ProgressCallback) =>
		apiBase<T>(path, "PUT", "0.2", body, progressCb),

	makeUrl: (...path: string[]) => makeApiUrl("v0.2", path),
};
